<template>
    <DashboardLayout>
        <div class="flex flex-column" style="min-height: calc(100vh - 100px);">
        <Breadcrumb class="flex breadcrumb" :model="items" />

        <!-- Filters -->
        <div class="card px-3 py-3 mb-0 filter-section">
            <div class="filter-grid">
                <div class="filter-field">
                    <label class="filter-label block mb-1">Subject</label>
                    <MultiSelect
                        v-model="selectedSubjects"
                        :options="subjectOptions"
                        optionLabel="label"
                        optionValue="value"
                        placeholder="All my subjects"
                        class="w-full filter-control"
                        overlayClass="pp-filter-overlay"
                        size="small"
                        variant="filled"
                        display="comma"
                        :maxSelectedLabels="2"
                        selectedItemsLabel="{0} subjects selected"
                        show-clear
                    />
                </div>
                <div class="filter-field">
                    <label class="filter-label block mb-1">Year</label>
                    <MultiSelect
                        v-model="selectedYears"
                        :options="yearOptions"
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Any year"
                        class="w-full filter-control"
                        overlayClass="pp-filter-overlay"
                        size="small"
                        variant="filled"
                        display="comma"
                        :maxSelectedLabels="2"
                        selectedItemsLabel="{0} years selected"
                        show-clear
                    />
                </div>
                <div class="filter-field" v-if="mediumOptions.length">
                    <label class="filter-label block mb-1">Medium</label>
                    <Select
                        v-model="selectedMedium"
                        :options="mediumOptions"
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Any medium"
                        class="w-full filter-control"
                        overlayClass="pp-filter-overlay"
                        size="small"
                        variant="filled"
                        show-clear
                    />
                </div>
                <div class="filter-field">
                    <label class="filter-label block mb-1">Term</label>
                    <Select
                        v-model="selectedTerm"
                        :options="termOptions"
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Any term"
                        class="w-full filter-control"
                        overlayClass="pp-filter-overlay"
                        size="small"
                        variant="filled"
                        show-clear
                    />
                </div>
            </div>

            <div class="filter-footer">
                <div class="filter-actions">
                    <Button label="Apply" icon="pi pi-filter" size="small"
                        class="filter-control filter-btn apply-button" @click="applyFilters" />
                    <Button label="Clear" icon="pi pi-times" size="small"
                        class="p-button-outlined filter-control filter-btn"
                        :disabled="!hasActiveFilters" @click="clearFilters" />
                </div>
            </div>
        </div>

        <div class="card p-4">
            <div v-if="papers.length === 0" class="flex justify-content-center py-8">
                <span class="Caption text-gray-700 text-center">{{ emptyStateText }}</span>
            </div>

            <div v-else>
                <div class="paper-grid">
                    <Card v-for="paper in papers" :key="paper._id" class="shadow-2 border-round-md paper-card">
                        <template #title>
                            <span class="H6-Medium paper-title truncate block" :title="paper.name">{{ paper.name }}</span>
                        </template>
                        <template #content>
                            <div class="paper-meta">
                                <div v-for="meta in paperMeta(paper)" :key="meta.label" class="paper-meta-row">
                                    <span class="meta-label">{{ meta.label }}</span>
                                    <span class="meta-value truncate" :title="meta.value">{{ meta.value }}</span>
                                </div>
                            </div>

                            <div class="file-list">
                                <div v-if="!paper.files?.length" class="Label file-empty">
                                    No files attached yet.
                                </div>
                                <div v-for="fileGroup in fileGroups(paper)" :key="fileGroup.kind" class="file-row">
                                    <span class="file-row-label truncate" :title="fileGroup.names.join(', ')">
                                        {{ fileGroup.label }}
                                    </span>
                                    <span v-if="fileGroup.files.length > 1" class="file-row-count">
                                        {{ fileGroup.files.length }}
                                    </span>
                                    <Button icon="pi pi-download" text rounded class="file-action"
                                        :title="fileGroup.downloadHint"
                                        :aria-label="fileGroup.downloadHint"
                                        @click="downloadKind(paper, fileGroup.kind)" />
                                </div>
                            </div>
                        </template>
                    </Card>
                </div>

                <div v-if="pagination.totalPages > 1" class="flex justify-content-center pt-4">
                    <Paginator
                        :rows="pagination.limit"
                        :totalRecords="pagination.totalRecords"
                        :first="(pagination.currentPage - 1) * pagination.limit"
                        @page="onPageChange"
                    />
                </div>
            </div>
        </div>

        <Toast />

        <!-- Disclaimer -->
        <div class="text-center w-full max-w-xl mx-auto pt-4 px-6 mt-auto">
            <h3 class="disclaimer-heading text-lg font-semibold mb-2" style="color: #dc2626;">Disclaimer</h3>
            <p class="text-700 text-sm m-0">
                All educational materials are for personal use only and may not be copied, distributed, or resold.
            </p>
        </div>

        <!-- Can't find a past paper -->
            <div class="text-center w-full max-w-xl mx-auto pt-2 pb-4 px-6">
                <p class="text-700 text-sm m-0">
                    Can't find a past paper? Email us at
                    <span class="support-link cursor-pointer px-1" @click="goToContact">support@ilearndsl.com</span>
                    and we'll try our best to upload it.
                </p>
            </div>
        </div>

    </DashboardLayout>
</template>

<script setup>
import { onMounted, ref, computed } from "vue";
import { useRouter } from "vue-router";
import Breadcrumb from 'primevue/breadcrumb';
import Card from 'primevue/card';
import Button from 'primevue/button';
import Toast from 'primevue/toast';
import Select from 'primevue/select';
import MultiSelect from 'primevue/multiselect';
import Paginator from 'primevue/paginator';
import DashboardLayout from "@/layout/DashboardLayout.vue";
import { listPapers, fetchPaperFilters } from "@/api/papers";
import { notifyError, notifyWarn } from "@/utils/notification";
import { cleanupLables, formatSubjectName } from "@/utils/cleanups";
import { downloadPaperFilesOfKind, kindMeta, fileDisplayName, filesOfKind } from "@/utils/paperFiles";
import { getProfile } from "@/api/student";


const router = useRouter();

const items = ref([
    { label: 'Dashboard' },
    { label: 'Past Papers' },
]);

const goToContact = () => {
    router.push({ path: '/aboutUs', query: { scrollTo: 'contact-form' } });
};

const papers = ref([]);
const emptyStateText = ref('Loading papers...');
const pagination = ref({ totalRecords: 0, currentPage: 1, totalPages: 0, limit: 20 });

// The student's own grade and subjects scope every request — a student browses their own
// syllabus, not the whole library.
const studentGrade = ref(null);
const studentSubjects = ref([]);

const selectedSubjects = ref([]);
const selectedYears = ref([]);
const selectedMedium = ref(null);
const selectedTerm = ref(null);

const subjectOptions = ref([]);
const yearOptions = ref([]);
const mediumOptions = ref([]);
const termOptions = ref([]);

const hasActiveFilters = computed(() =>
    selectedSubjects.value.length > 0 ||
    selectedYears.value.length > 0 ||
    !!selectedMedium.value ||
    !!selectedTerm.value
);

const baseFilters = () => {
    const filters = {};
    if (studentGrade.value) filters.grade = studentGrade.value;
    // With no explicit subject picked, fall back to the subjects on the student's profile.
    const subjects = selectedSubjects.value.length ? selectedSubjects.value : studentSubjects.value;
    if (subjects.length) filters.subject = subjects;
    return filters;
};

const buildFilters = (page = 1) => {
    const filters = { ...baseFilters(), page, limit: pagination.value.limit, sort: 'year_desc' };
    if (selectedYears.value.length) filters.year = selectedYears.value;
    if (selectedMedium.value) filters.medium = selectedMedium.value;
    if (selectedTerm.value) filters.term = selectedTerm.value;
    return filters;
};

/**
 * Options come from the papers that exist within the student's grade, so a year in the dropdown
 * always has something behind it.
 */
const loadFilterOptions = async () => {
    try {
        const res = await fetchPaperFilters(studentGrade.value ? { grade: studentGrade.value } : {});
        const data = res.data?.data || {};

        // Subject choices are the intersection of what the student studies and what exists.
        const available = data.subjects || [];
        subjectOptions.value = available
            .filter(s => studentSubjects.value.length === 0
                || studentSubjects.value.includes(s.code)
                || studentSubjects.value.includes(s.name))
            .map(s => ({ label: formatSubjectName(s.name), value: s.code }));

        yearOptions.value = (data.years || []).map(y => ({ label: `${y.year}`, value: y.year }));
        mediumOptions.value = (data.mediums || []).map(m => ({ label: m.name, value: m.code }));
        termOptions.value = (data.terms || []).map(t => ({ label: cleanupLables(t.term), value: t.term }));
    } catch {
        // Filter options are a convenience; a failure here should not block the paper list.
        subjectOptions.value = [];
        yearOptions.value = [];
        mediumOptions.value = [];
        termOptions.value = [];
    }
};

const loadProfile = async () => {
    try {
        const user = await getProfile();
        studentGrade.value = user.data?.data?.currentEducationLevel || null;
        const subjects = user.data?.data?.interestedSubjects;
        studentSubjects.value = Array.isArray(subjects) ? subjects : (subjects ? [subjects] : []);
    } catch {
        studentGrade.value = null;
        studentSubjects.value = [];
    }
};

const fetchFiles = async (page = 1) => {
    emptyStateText.value = 'Loading papers...';
    try {
        const res = await listPapers(buildFilters(page));
        const data = res.data?.data || {};
        papers.value = data.papers || [];
        pagination.value = data.pagination || pagination.value;

        if (papers.value.length === 0) {
            emptyStateText.value = hasActiveFilters.value
                ? 'No papers found for the selected filters.'
                : 'No papers found for your grade and subjects.';
        }
    } catch (err) {
        papers.value = [];
        const responseData = err?.response?.data;
        const errorMsg = responseData?.message || "An error occurred while fetching past papers. Please try again.";

        if (err?.response?.status === 403) {
            emptyStateText.value = errorMsg;
            notifyWarn("Subscription Required", errorMsg);
        } else {
            emptyStateText.value = "An error occurred while fetching past papers. Please try again.";
            notifyError(responseData?.status || "Error", errorMsg);
        }
    }
};

const applyFilters = () => fetchFiles(1);

const clearFilters = () => {
    selectedSubjects.value = [];
    selectedYears.value = [];
    selectedMedium.value = null;
    selectedTerm.value = null;
    fetchFiles(1);
};

const onPageChange = (event) => fetchFiles(event.page + 1);

// Only the metadata a paper actually has — a blank value is left out rather than rendered
// as a label with nothing after it.
const paperMeta = (paper) => [
    { label: 'Grade', value: paper.gradeName ? cleanupLables(paper.gradeName) : '' },
    { label: 'Subject', value: paper.subject ? formatSubjectName(paper.subject) : '' },
    { label: 'Year', value: paper.year ? String(paper.year) : '' },
    { label: 'Term', value: paper.term ? cleanupLables(paper.term) : '' },
    { label: 'Medium', value: paper.medium ? cleanupLables(paper.medium) : '' },
].filter(meta => meta.value);

// A paper can carry several files of one kind. Grouping them bounds the card at one row per
// kind, and the whole row downloads together rather than spilling buttons across the card.
const KIND_ORDER = ['paper', 'marking_scheme', 'answer_sheet'];

const fileGroups = (paper) => {
    const files = paper.files || [];
    const kinds = [...new Set([...KIND_ORDER, ...files.map(f => f.kind)])];
    return kinds
        .map(kind => ({ kind, files: filesOfKind(paper, kind) }))
        .filter(group => group.files.length)
        .map(group => {
            const { shortLabel, icon } = kindMeta(group.kind);
            const count = group.files.length;
            // The file names carry the admin's labels, so they are worth surfacing on hover
            // even though the row itself only shows the kind.
            const names = group.files.map((file, index) => fileDisplayName(file, index, count));
            return {
                ...group,
                icon,
                names,
                label: count > 1 ? `${shortLabel}s` : shortLabel,
                downloadHint: count > 1 ? `Download all ${count} ${shortLabel.toLowerCase()}s` : `Download ${names[0]}`,
            };
        });
};

const downloadKind = (paper, kind) => downloadPaperFilesOfKind(paper, kind);

onMounted(async () => {
    await loadProfile();
    await loadFilterOptions();
    await fetchFiles();
})
</script>

<style scoped>
.breadcrumb {
    display: flex !important;
    flex-wrap: wrap !important;
    overflow-x: hidden !important;
}

.filter-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(12rem, 100%), 1fr));
    align-items: end;
    gap: 0.75rem 1rem;
}

.filter-field {
    min-width: 0;
}

.filter-footer {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem 1.5rem;
    margin-top: 1rem;
}

.filter-actions {
    display: flex;
    gap: 0.5rem;
    margin-left: auto;
}

@media (max-width: 575px) {
    .filter-actions {
        width: 100%;
        margin-left: 0;
    }
}

.filter-section .filter-label {
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--p-text-muted-color, #64748b);
    text-transform: uppercase;
    letter-spacing: 0.03em;
}

.filter-section :deep(.filter-control) {
    height: 2.25rem;
    min-height: 2.25rem;
    font-size: 0.8rem !important;
    align-items: center;
}

.filter-section :deep(.p-multiselect-label-container) {
    height: 100%;
    display: flex;
    align-items: center;
}

.filter-section :deep(.p-select-label),
.filter-section :deep(.p-multiselect-label) {
    height: 100%;
    display: flex;
    align-items: center;
    padding-top: 0;
    padding-bottom: 0;
    font-size: 0.8rem !important;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    min-width: 0;
}

.filter-section :deep(.filter-btn) {
    width: 7.5rem;
    flex: 0 0 7.5rem;
    white-space: nowrap;
}

@media (max-width: 575px) {
    .filter-section :deep(.filter-btn) {
        width: auto;
        flex: 1 1 0;
    }
}

.filter-section :deep(.p-button .p-button-label),
.filter-section :deep(.p-button .p-button-icon) {
    font-size: 0.8rem !important;
}

.apply-button {
    background-color: #dc2626;
    border-color: #dc2626;
}

.apply-button:hover {
    background-color: #b91c1c !important;
    border-color: #b91c1c !important;
}

/* Equal-height cards */
.paper-grid {
    display: grid;
    grid-template-columns: 1fr;
    grid-auto-rows: 1fr;
    gap: 1rem;
}

@media (min-width: 576px) {
    .paper-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (min-width: 992px) {
    .paper-grid {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }
}

@media (min-width: 1200px) {
    .paper-grid {
        grid-template-columns: repeat(4, minmax(0, 1fr));
    }
}

.paper-card {
    padding: 1.15rem 1.25rem;
    height: 100%;
    min-width: 0;
    display: flex;
    flex-direction: column;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    background-color: var(--surface-card);
}

.paper-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
}

.paper-card :deep(.p-card-body),
.paper-card :deep(.p-card-content) {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.paper-card :deep(.p-card-caption) {
    flex: 0 0 auto;
}

.paper-title {
    font-size: 1.2rem;
    line-height: 1.5;
    font-weight: 500;
}

.paper-meta {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    flex: 0 0 auto;
    padding: 0.15rem 0.25rem 0;
}

.paper-meta-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    align-items: baseline;
    min-width: 0;
}

.meta-label {
    min-width: 0;
    font-size: 0.72rem;
    font-weight: 600;
    line-height: 1.35;
    color: var(--gray-700);
    text-transform: uppercase;
    letter-spacing: 0.02em;
}

.meta-value {
    min-width: 0;
    font-size: 0.8rem;
    line-height: 1.35;
    color: var(--gray-600);
}

.file-list {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    margin-top: auto;
    padding-top: 0.6rem;
    flex: 0 1 auto;
    min-height: 0;
    overflow-y: auto;
}

.file-empty {
    color: var(--gray-700);
    font-size: 0.8rem;
    padding: 0.35rem 0.25rem;
}

.file-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.2rem 0.25rem;
    border-radius: 6px;
    min-height: 2rem;
}

.file-row-label {
    font-size: 0.8rem;
    font-weight: 500;
    color: var(--gray-600);
    flex: 1 1 auto;
    min-width: 0;
}

.file-row-count {
    flex: 0 0 auto;
    font-size: 0.7rem;
    line-height: 1;
    color: var(--gray-600);
    background: var(--gray-200, #f3f4f6);
    border-radius: 1rem;
    padding: 0.2rem 0.4rem;
}

.file-list :deep(.file-action) {
    width: 1.75rem;
    height: 1.75rem;
    padding: 0;
    flex: 0 0 auto;
    margin-left: auto;
    color: var(--gray-600);
}

.file-list :deep(.file-action:hover),
.file-list :deep(.file-action:focus-visible) {
    color: var(--gray-600);
    background: var(--gray-300, #dbdfe3);
}

.file-list :deep(.file-action .p-button-icon) {
    font-size: 0.85rem;
}

.support-link {
    color: #991b1b;
    text-decoration: underline;
}

.support-link:hover {
    color: #dc2626;
}

.text-gray-700 {
    color: var(--gray-700) !important;
}

.truncate {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
</style>

<style>
.pp-filter-overlay .p-select-option,
.pp-filter-overlay .p-multiselect-option {
    font-size: 0.8rem;
    padding-top: 0.4rem;
    padding-bottom: 0.4rem;
}
</style>
